"use client";

import { BookOpen, Users, Video } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Prevention() {
  const heading = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const resources = [{
    icon: BookOpen,
    title: "Proactive Facial Balancing",
    description: "Learn how strategic, micro-dosing of neurotoxins and fillers can prevent deep-set lines before they form, maintaining your natural expression.",
    badge: "Preventative Care"
  }, {
    icon: Users,
    title: "Concierge Consultations",
    description: "Private, one-on-one sessions with Dr. Amber Ojeda, DNP, RN, FNP-C to discuss your unique facial anatomy and create a long-term, refined aesthetic roadmap.",
    badge: "Personalized Strategy"
  }, {
    icon: Video,
    title: "The Art of Refinement",
    description: "Access our curated library of educational content on facial symmetry, skin health, and the science behind natural-looking results.",
    badge: "Expert Insights"
  }];
  return <section data-section-id="14709" id="the-power-of-prevention" className="dark bg-background py-24 md:py-32">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10 lg:px-16">
        <div ref={heading.ref} className={`mx-auto mb-20 max-w-[720px] text-center transition-all duration-700 ease-out ${heading.isVisible ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"}`}>
          <p className="mb-6 text-[0.8125rem] font-medium uppercase tracking-[0.12em] text-muted-foreground font-default">
            Refined by Design
          </p>
          <h2 className="font-serif text-[clamp(2rem,3.8vw,3.25rem)] font-normal leading-[1.12] tracking-[-0.015em] text-foreground">
            Prevention is the ultimate{" "}
            <em className="italic font-normal" style={{
            color: "var(--chart-1)"
          }}>
              luxury
            </em>
          </h2>
          <p className="mt-6 text-[clamp(1.125rem,1.3vw,1.25rem)] leading-[1.65] text-muted-foreground font-default">
            We believe in subtle, preventative care that honors your unique features, ensuring you look like the most refreshed version of yourself.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {resources.map((resource, i) => {
          const Icon = resource.icon;
          return <ResourceCard key={i} index={i} icon={Icon} title={resource.title} description={resource.description} badge={resource.badge} />;
        })}
        </div>

        <div className="mt-16 flex flex-col items-center justify-center gap-3 text-center md:flex-row md:gap-4">
          <p className="text-[clamp(1.125rem,1.3vw,1.25rem)] leading-[1.65] text-foreground font-default">
            Ready to start your journey toward refined balance?
          </p>
          <Link to="https://my.portraitcare.com/booking/?business_id=jr3uOp" className="text-[clamp(1.125rem,1.3vw,1.25rem)] leading-[1.65] text-foreground font-default underline decoration-[1.5px] underline-offset-[6px] transition-all duration-200 hover:decoration-[2.5px]" style={{
          textDecorationColor: "var(--chart-1)"
        }}>
            Schedule your consultation →
          </Link>
        </div>
      </div>
    </section>;
}
type ResourceCardProps = {
  index: number;
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  description: string;
  badge: string;
};
function ResourceCard({
  index,
  icon: Icon,
  title,
  description,
  badge
}: ResourceCardProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} className={`group flex flex-col rounded-lg bg-card p-8 shadow-sm transition-all duration-700 ease-out hover:-translate-y-0.5 hover:shadow-lg md:p-10 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"}`} style={{
    transitionDelay: isVisible ? `${index * 80}ms` : "0ms"
  }}>
      <div className="mb-7 flex h-12 w-12 items-center justify-center rounded-lg transition-colors duration-300" style={{
      backgroundColor: "var(--accent)"
    }}>
        <Icon className="h-6 w-6" strokeWidth={1.5} style={{
        color: "var(--chart-1)"
      } as React.CSSProperties} />
      </div>

      <h3 className="mb-4 text-[1.25rem] font-medium leading-[1.35] tracking-[-0.005em] text-foreground font-default">
        {title}
      </h3>

      <p className="mb-8 flex-1 text-base leading-[1.7] text-muted-foreground font-default">
        {description}
      </p>

      <div className="inline-flex w-fit items-center rounded-lg px-3 py-1 text-[0.8125rem] font-medium font-default" style={{
      backgroundColor: "var(--accent)",
      color: "var(--accent-foreground)"
    }}>
        {badge}
      </div>
    </div>;
}